import { Navigate, useParams, useLocation } from "react-router-dom";
import usePaginate from "@/components/paginate/usePaginate";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import {
  getStreamlitNotebookJobRunsList,
  getStreamlitNotebookJobRunsSummary,
} from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { RelativeComponentLoader } from "@/components/loader";
import { Stack } from "@altimateai/lego";
import { CommonRunsListTable } from "@/pages/query/common/tables/CommonRunsListTable";
import { CommonRunsCostChart } from "@/pages/query/common/charts/CommonRunsCostChart";
import { WorkloadType } from "@/pages/query/types";
import { CommonSummary } from "@/pages/query/common/CommonSummary";
import { toast, Typography } from "@altimateai/lego";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";

export const StreamlitRunHistory = () => {
  const { hash } = useParams();
  const { state } = useLocation();
  const { page, setPage, pageSize, updatePageSize } = usePaginate({ size: 20 });
  const { startDate, endDate } = useDateFromSearchParams();

  useBreadcrumb(getEntityName(state) ?? hash ?? "Job Details", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "Streamlit", pathname: "/query/streamlit" },
    ],
  });

  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["streamlit_job", hash, startDate, endDate],
    queryFn: () =>
      getStreamlitNotebookJobRunsSummary(hash!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workload_type: WorkloadType.STREAMLIT,
      }),
    enabled: Boolean(hash),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["streamlit_jobs", "streamlit_runs", hash, startDate, endDate],
    queryFn: () =>
      getStreamlitNotebookJobRunsList(hash!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        workload_type: WorkloadType.STREAMLIT,
      }),
    enabled: Boolean(hash),
  });

  if (isLoading || isSummaryLoading) {
    return <RelativeComponentLoader label="Loading..." />;
  }

  if (!data || !summaryData) {
    // If no data is found, redirect to the main streamlit page
    toast({
      variant: "destructive",
      description: "No runs found for this streamlit job.",
    });
    return <Navigate to="/query/streamlit" />;
  }

  return (
    <Stack direction="column" gap={"lg"}>
      <CommonSummary summaryData={summaryData} />
      <CommonRunsCostChart data={data} />
      <Stack direction={"column"} gap={"lg"} className={"al-mt-4"}>
        <Typography variant={"section-heading"}>Runs list</Typography>
        <CommonRunsListTable
          basePath={"streamlit"}
          tableData={data}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          updatePageSize={updatePageSize}
        />
      </Stack>
    </Stack>
  );
};
